import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { userAPI } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import Layout from "../common/Layout";

const Users = () => {
  // Get auth and profile data from context
  const { currentUser, userProfile } = useAuth();
  // Use Firestore profile if available, otherwise Firebase Auth claims
  const effectiveUser = userProfile || currentUser || {};
  const effectiveUserId =
    effectiveUser.id || effectiveUser._id || effectiveUser.uid;

  // Determine which roles current user can manage
  const roleStr = (effectiveUser.role || "").toString().toLowerCase();
  const manageableRoles = roleStr.includes("developer")
    ? ["admin"]
    : roleStr.includes("admin")
    ? ["faculty", "scholar", "student", "user"]
    : [];

  // State for users
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Roles dropdown options
  const roles = ["all", ...manageableRoles];

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedUser, setSelectedUser] = useState({
    name: "",
    email: "",
    password: "",
    role: manageableRoles[0] || "",
    universityId: "",
    active: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  // Fetch users data
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userAPI.getAll();
      // Normalize 'active' status (fallback to legacy 'isActive')
      const normalized = response.data.map((u) => ({
        ...u,
        active: u.active ?? u.isActive,
        _id: u.id, // map Firestore id to _id for table and delete
      }));
      // Only include users in manageable roles
      const managed = normalized.filter((u) =>
        manageableRoles.includes(u.role)
      );
      setUsers(managed);
      setFilteredUsers(managed);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog opening for adding or editing a user
  const handleOpenDialog = (mode, userData = null) => {
    setDialogMode(mode);
    if (mode === "edit" && userData) {
      const { password, ...userWithoutPassword } = userData;
      setSelectedUser({
        ...userWithoutPassword,
        password: "",
      });
    } else {
      setSelectedUser({
        name: "",
        email: "",
        password: "",
        role: manageableRoles[0] || "", // default role
        universityId: "",
        active: true,
      });
    }
    setDialogOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUser({
      ...selectedUser,
      [name]: value,
    });
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Handle form submission for adding or editing a user
  const handleSubmit = async () => {
    setError(null);
    setSuccessMsg(null);
    setLoading(true);
    try {
      let response;
      const payload = {
        name: selectedUser.name,
        email: selectedUser.email,
        universityId: selectedUser.universityId,
        departmentId: effectiveUser.departmentId,
        active: selectedUser.active,
      };
      if (dialogMode === "add") {
        // Use role-specific endpoint
        response = await {
          admin: userAPI.createAdmin,
          faculty: userAPI.createFaculty,
          scholar: userAPI.createScholar,
          student: userAPI.createStudent,
          user: userAPI.createUser,
        }[selectedUser.role](payload);
        // Extract created user object
        const newUser =
          response.data.user || response.data.admin || response.data;
        // Normalize id field
        newUser._id = newUser.id;
        newUser.active = newUser.active ?? newUser.isActive;
        setUsers((prev) => [...prev, newUser]);
        setFilteredUsers((prev) => [...prev, newUser]);
        // Use server-provided message and temp password if available
        const serverMessage = response.data.message;
        const tempPassword = response.data.tempPassword;
        let msg =
          serverMessage || `User '${newUser.name}' created successfully.`;
        if (tempPassword) msg += ` Temporary password: ${tempPassword}`;
        setSuccessMsg(msg);
      } else {
        response = await userAPI.update(selectedUser._id, selectedUser);
        const updatedUser = response.data;
        updatedUser._id = updatedUser.id;
        updatedUser.active = updatedUser.active ?? updatedUser.isActive;
        setUsers((prev) =>
          prev.map((u) => (u._id === updatedUser._id ? updatedUser : u))
        );
        setFilteredUsers((prev) =>
          prev.map((u) => (u._id === updatedUser._id ? updatedUser : u))
        );
        setSuccessMsg(`User '${updatedUser.name}' updated successfully.`);
      }
      setDialogOpen(false);
    } catch (err) {
      console.error("[Users] Error in handleSubmit:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle user deletion
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const response = await userAPI.delete(id);
      console.log("[Users] Delete user response:", response);
      const updatedUsers = users.filter((user) => user._id !== id);
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      setSuccessMsg(`User deleted successfully.`);
    } catch (err) {
      console.error("[Users] Error deleting user:", err);
      setError(
        err.response?.data?.message ||
          "Failed to delete user. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get color for role chip
  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "primary";
      case "faculty":
        return "secondary";
      case "scholar":
        return "success";
      case "student":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Layout title="User Management">
      {/* Display messages */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage user accounts and their permissions
        </Typography>
      </Box>

      {/* Filters & Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel id="role-filter-label">Role</InputLabel>
              <Select
                labelId="role-filter-label"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                label="Role"
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role === "all"
                      ? "All Roles"
                      : role.charAt(0).toUpperCase() + role.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={loadUsers}
              variant="outlined"
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button
              fullWidth
              startIcon={<PersonAddIcon />}
              onClick={() => handleOpenDialog("add")}
              variant="contained"
              color="primary"
            >
              Add User
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 650 }} aria-label="users table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow key={user._id}>
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{ mr: 2, bgcolor: getRoleColor(user.role) }}
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            {user.name}
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              user.role.charAt(0).toUpperCase() +
                              user.role.slice(1)
                            }
                            size="small"
                            color={getRoleColor(user.role)}
                          />
                        </TableCell>
                        <TableCell>{user.department}</TableCell>
                        <TableCell>{user.universityId}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.active ? "Active" : "Inactive"}
                            color={user.active ? "success" : "default"}
                            size="small"
                            variant={user.active ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog("edit", user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(user._id)}
                              disabled={user._id === effectiveUserId} // Can't delete current user
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "add" ? "Add New User" : "Edit User"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={selectedUser.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={selectedUser.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            {dialogMode === "add" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={selectedUser.password}
                  onChange={handleInputChange}
                  required={dialogMode === "add"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  name="role"
                  value={selectedUser.role}
                  onChange={handleInputChange}
                  label="Role"
                >
                  {manageableRoles.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="University ID"
                name="universityId"
                value={selectedUser.universityId}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="active"
                  value={selectedUser.active}
                  onChange={(e) =>
                    setSelectedUser({
                      ...selectedUser,
                      active: e.target.value,
                    })
                  }
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {dialogMode === "add" ? "Add User" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Users;
